import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { db, auth, app } from "../../lib/firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getCached, setCached, shouldRevalidate } from "../../utils/sessionCache";
import { logEvent } from "../../utils/eventBatcher";
import { useAuthContext } from "../../contexts/AuthContext";
import { issueCertificate } from "../../services/certificateService";
import {
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  ArrowLeftIcon,
  ChevronRightIcon
} from "../../components/icons/OutlineIcons";
import { GraduationCap, LogOut, User, BookOpen, Mail, BarChart3, FileText, Download, Menu, X, ChevronDown, ChevronUp, List, Flag } from "lucide-react";
import AuthModal from "../../components/AuthModal";
import OptimizedVideoPlayer from "../../components/OptimizedVideoPlayer";

export default function LearningPlayer() {
  const { id, lessonId } = useParams();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const instructorIdFromUrl = searchParams.get('instructorId');

  const [course, setCourse] = useState<any>(null);
  const [instructor, setInstructor] = useState<any>(null);
  const [lesson, setLesson] = useState<any>(null);
  const [lessons, setLessons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<Record<string, any>>({});
  const [watchTime, setWatchTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLessonList, setShowLessonList] = useState(false);
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingLessonId, setReportingLessonId] = useState<string | null>(null);
  const [reportMessage, setReportMessage] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const playerRef = useRef<HTMLVideoElement>(null);
  
  const themeColor = instructor?.themeColor || "#8B5CF6";

  const currentIndex = Number(lessonId);

  useEffect(() => {
    async function load() {
      if (!id || !user) return;

      // Cache-first: Load course
      const courseCacheKey = `course_${id}`;
      let courseData = getCached(courseCacheKey);
      
      if (!courseData || shouldRevalidate(courseCacheKey)) {
        const courseSnap = await getDoc(doc(db, "courses", id));
        if (courseSnap.exists()) {
          courseData = courseSnap.data();
          setCached(courseCacheKey, courseData, courseData.updatedAt?.seconds);
        }
      }
      
      if (courseData) {
        setCourse(courseData);
        
        // Cache-first: Load instructor info
        const instructorIdToLoad = instructorIdFromUrl || courseData.instructorId;
        if (instructorIdToLoad) {
          const instructorCacheKey = `instructor_${instructorIdToLoad}`;
          let instructorData = getCached(instructorCacheKey);
          
          if (!instructorData || shouldRevalidate(instructorCacheKey)) {
            const instructorSnap = await getDoc(doc(db, "users", instructorIdToLoad));
            if (instructorSnap.exists()) {
              instructorData = instructorSnap.data();
              setCached(instructorCacheKey, instructorData);
            }
          }
          
          if (instructorData) {
            setInstructor(instructorData);
            // Update document title with instructor's brand name (only for Pro Yearly and Pro+ Yearly)
            const tier = instructorData.subscriptionTier || '';
            const isYearlyPlan = tier === 'pro_yearly' || tier === 'pro_plus_yearly';
            if (isYearlyPlan) {
              const instructorName = instructorData.displayName || instructorData.businessName || 'EduMi LMS';
              document.title = instructorName;
            } else {
              document.title = 'EduMi LMS';
            }
          }
        }
      }

      // Cache-first: Load lessons
      const lessonsCacheKey = `lessons_${id}`;
      let lessonsData = getCached(lessonsCacheKey);
      
      if (!lessonsData || shouldRevalidate(lessonsCacheKey)) {
        const q = query(collection(db, "lessons"), where("courseId", "==", id));
        const snap = await getDocs(q);
        lessonsData = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.orderIndex - b.orderIndex);
        setCached(lessonsCacheKey, lessonsData);
      }

      setLessons(lessonsData);

      // Load current lesson
      const l = lessonsData.find((x: any) => x.orderIndex === Number(lessonId));
      setLesson(l);

      // Cache-first: Load progress for all lessons (only completed status)
      const progressCacheKey = `progress_${user.uid}_${id}`;
      let progressData = getCached(progressCacheKey);
      
      if (!progressData || shouldRevalidate(progressCacheKey)) {
        progressData = {};
        for (const lesson of lessonsData) {
          const progressRef = doc(db, "progress", `${user.uid}_${id}_${lesson.id}`);
          const progressSnap = await getDoc(progressRef);
          if (progressSnap.exists()) {
            progressData[lesson.id] = progressSnap.data();
          }
        }
        setCached(progressCacheKey, progressData);
      }
      
      setProgress(progressData);


      setLoading(false);
    }

    load();
  }, [id, lessonId, user, instructorIdFromUrl]);





  // Mark lesson completed and auto-play next
  const markCompleted = async () => {
    if (!user || !lesson || !id) return;

    const ref = doc(db, "progress", `${user.uid}_${id}_${lesson.id}`);

    // Idempotency check: only write if not already completed
    const existingProgress = progress[lesson.id];
    if (existingProgress?.completed) {
      console.log('Lesson already completed, skipping write');
      // Still proceed to next lesson logic
    } else {
      // Single write on explicit completion
      await setDoc(ref, {
        userId: user.uid,
        courseId: id,
        lessonId: lesson.id,
        completed: true,
        completedAt: Timestamp.now(),
        lastWatchedTime: Math.round(watchTime)
      });

      // Log completion event (batched)
      logEvent('lesson_completed', user.uid, {
        courseId: id,
        lessonId: lesson.id,
        lessonTitle: lesson.title
      });
    }

    // Update local progress state and cache
    const newProgress = {
      ...progress,
      [lesson.id]: { 
        completed: true, 
        completedAt: Timestamp.now(),
        lastWatchedTime: Math.round(watchTime)
      }
    };
    setProgress(newProgress);
    setCached(`progress_${user.uid}_${id}`, newProgress);

    // Auto-play next lesson
    const next = lessons.find((l) => l.orderIndex === currentIndex + 1);
    if (next) {
      setTimeout(() => {
        navigate(`/courses/${id}/learn/${next.orderIndex}`);
      }, 1000);
    } else {
      // Last lesson - check if instructor's tier allows certificates
      setTimeout(async () => {
        try {
          if (!id) return;

          const courseSnap = await getDoc(doc(db, "courses", id));
          const courseData = courseSnap.exists() ? courseSnap.data() : null;
          
          // Use instructor from state (already loaded with priority to URL param)
          const instructorId = instructor?.uid || courseData?.instructorId;
          const tier = instructor?.subscription?.tier || "free";
          
          // Only Pro+ and above can issue certificates
          if (tier === "free" || tier === "pro") {
            alert("🎉 ยินดีด้วย! คุณเรียนจบทุกบทแล้ว\n\nหากต้องการใบประกาศนียบัตร กรุณาติดต่อผู้สอนโดยตรงครับ");
            return;
          }

          // Validate all lessons are truly completed
          const allLessonsCompleted = lessons.every(l => progress[l.id]?.completed);
          const completedCount = lessons.filter(l => progress[l.id]?.completed).length;
          
          if (!allLessonsCompleted) {
            alert(`❌ คุณยังเรียนไม่ครบทุกบท\n\nเรียนจบแล้ว: ${completedCount}/${lessons.length} บท\nกรุณาเรียนให้ครบก่อนรับใบประกาศนียบัตร`);
            return;
          }

          const shouldGetCert = window.confirm(
            "🎉 ยินดีด้วย! คุณเรียนจบทุกบทแล้ว\n\nต้องการรับใบประกาศนียบัตรหรือไม่?"
          );
          if (!shouldGetCert) return;

          const certQ = query(
            collection(db, "certificates"),
            where("userId", "==", user.uid),
            where("courseId", "==", id)
          );
          const certSnap = await getDocs(certQ);

          if (!certSnap.empty) {
            navigate(`/certificates/${certSnap.docs[0].id}`);
            return;
          }

          const instructorName = instructor?.name || instructor?.email?.split("@")[0] || "";
          const userName = (user as any).name || user.email?.split("@")[0] || "";

          const certificateId = await issueCertificate(
            user.uid,
            userName,
            id,
            courseData?.title || course?.title || "",
            instructorId || "",
            instructorName || courseData?.instructorName || "",
            new Date()
          );

          navigate(`/certificates/${certificateId}`);
        } catch (e) {
          navigate(`/certificate/${id}`);
        }
      }, 1000);
    }
  };

  const trackFileDownload = async (mediaId: string) => {
    if (!mediaId) return;
    
    try {
      const functions = getFunctions(app, "us-central1");
      const trackActivity = httpsCallable(functions, "trackMediaActivity");
      await trackActivity({ mediaId, activityType: "download" });
      console.log("✅ File download tracked:", mediaId);
    } catch (error) {
      console.error("Failed to track file download:", error);
    }
  };

  const handleFileDownload = (file: any) => {
    if (file.mediaId) {
      trackFileDownload(file.mediaId);
    }
    
    // Download with custom filename with "Download - " prefix
    const baseName = file.displayName || "ไฟล์ประกอบการเรียน";
    const downloadName = `Download - ${baseName}`;
    const link = document.createElement('a');
    link.href = file.url;
    link.download = downloadName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getProgressPercentage = (lessonId: string) => {
    const prog = progress[lessonId];
    if (!prog) return 0;
    if (prog.completed) return 100;
    return 0;
  };

  const isLessonCompleted = (lessonId: string) => {
    return progress[lessonId]?.completed || false;
  };

  const canCompleteLesson = true; // Always allow manual completion

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดบทเรียน...</p>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-textDark mb-4">ไม่พบบทเรียน</p>
          <Link to={`/courses/${id}`} className="text-primary hover:underline">
            ← กลับไปหน้าคอร์ส
          </Link>
        </div>
      </div>
    );
  }

  const completedCount = lessons.filter(l => isLessonCompleted(l.id)).length;
  const totalLessons = lessons.length;
  const courseProgress = Math.round((completedCount / totalLessons) * 100);

  const toggleLessonExpand = (lessonId: string) => {
    setExpandedLessonId(expandedLessonId === lessonId ? null : lessonId);
  };

  const handleReportIssue = async () => {
    if (!user || !reportingLessonId || !reportMessage.trim()) return;

    setIsSubmittingReport(true);
    try {
      const reportingLesson = lessons.find(l => l.id === reportingLessonId);
      
      // Extract filename from videoUrl
      const videoUrl = reportingLesson?.videoUrl || "";
      const videoFileName = videoUrl ? decodeURIComponent(videoUrl.split('/').pop()?.split('?')[0] || '') : '';
      
      const reportRef = doc(collection(db, "video_reports"));
      await setDoc(reportRef, {
        userId: user.uid,
        userName: (user as any).name || user.email?.split("@")[0] || "ผู้ใช้",
        userEmail: user.email,
        courseId: id,
        courseName: course?.title || "",
        lessonId: reportingLessonId,
        lessonTitle: reportingLesson?.title || "",
        lessonOrderIndex: reportingLesson?.orderIndex || 0,
        videoUrl: videoUrl,
        videoFileName: videoFileName,
        videoJobId: reportingLesson?.videoJobId || null,
        message: reportMessage.trim(),
        status: "pending",
        createdAt: Timestamp.now(),
        instructorId: instructor?.uid || course?.instructorId || ""
      });

      alert("✅ ส่งรายงานปัญหาเรียบร้อยแล้ว\n\nทีมงานจะตรวจสอบและแก้ไขให้เร็วที่สุดครับ");
      setShowReportModal(false);
      setReportMessage("");
      setReportingLessonId(null);
    } catch (error) {
      console.error("Error reporting issue:", error);
      alert("❌ เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    } finally {
      setIsSubmittingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Navigation Menu */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`} className="flex items-center gap-3 hover:opacity-70 transition-all">
              {instructor?.profileImage ? (
                <img src={instructor.profileImage} alt={instructor.name} className="w-11 h-11 rounded-2xl object-cover shadow-sm" />
              ) : (
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-sm" style={{ background: themeColor }}>
                  {instructor?.name?.[0]?.toUpperCase() || "T"}
                </div>
              )}
              <div>
                <h2 className="font-semibold text-gray-900 font-sans text-sm">{instructor?.name || "ผู้สอน"}</h2>
                <p className="text-xs text-gray-500 font-sans flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  ผู้สอนมืออาชีพ
                </p>
              </div>
            </Link>
            
            <div className="hidden lg:flex items-center gap-2">
              <button
                onClick={() => {
                  const isSubdomain = window.location.hostname !== 'localhost' && 
                                     window.location.hostname !== '127.0.0.1' && 
                                     !window.location.hostname.startsWith('edumi.co') &&
                                     window.location.hostname.includes('.edumi.co');
                  
                  if (isSubdomain) {
                    navigate('/');
                  } else {
                    const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                    navigate(baseUrl);
                  }
                }}
                className="px-4 py-2 rounded-xl font-medium font-sans transition-all flex items-center gap-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              >
                <BookOpen className="w-4 h-4" />
                คอร์สทั้งหมด
              </button>
              {user && (
                <>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=my');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=my`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl font-medium font-sans transition-all flex items-center gap-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <User className="w-4 h-4" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=history');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=history`);
                      }
                    }}
                    className="px-4 py-2 rounded-xl font-medium font-sans transition-all flex items-center gap-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <Mail className="w-4 h-4" />
                    ประวัติการชำระเงิน
                  </button>
                </>
              )}
              <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-2xl border border-gray-200">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold" style={{ color: themeColor }}>{completedCount}</span>/{totalLessons} บทเรียน
                </div>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ width: `${courseProgress}%`, background: themeColor }}
                  ></div>
                </div>
                <span className="text-sm font-semibold" style={{ color: themeColor }}>{courseProgress}%</span>
              </div>
              {user ? (
                <button
                  onClick={() => auth.signOut()}
                  className="px-4 py-2 bg-red-50 hover:bg-red-100 rounded-2xl font-medium font-sans text-red-600 transition-all flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  ออกจากระบบ
                </button>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-4 py-2 rounded-2xl font-medium font-sans transition-all text-white flex items-center gap-2 shadow-sm hover:shadow-md"
                  style={{ background: themeColor }}
                >
                  <User className="w-4 h-4" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {showMobileMenu && (
            <div className="lg:hidden mt-4 pt-4 border-t border-gray-100 space-y-2 animate-fadeIn">
              <button
                onClick={() => {
                  const isSubdomain = window.location.hostname !== 'localhost' && 
                                     window.location.hostname !== '127.0.0.1' && 
                                     !window.location.hostname.startsWith('edumi.co') &&
                                     window.location.hostname.includes('.edumi.co');
                  
                  if (isSubdomain) {
                    navigate('/');
                  } else {
                    const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                    navigate(baseUrl);
                  }
                  setShowMobileMenu(false);
                }}
                className="w-full px-4 py-3 rounded-xl font-medium font-sans transition-all flex items-center gap-3 text-gray-700 hover:bg-gray-50"
              >
                <BookOpen className="w-5 h-5" />
                คอร์สทั้งหมด
              </button>
              {user && (
                <>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=my');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=my`);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 rounded-xl font-medium font-sans transition-all flex items-center gap-3 text-gray-700 hover:bg-gray-50"
                  >
                    <User className="w-5 h-5" />
                    คอร์สของฉัน
                  </button>
                  <button
                    onClick={() => {
                      const isSubdomain = window.location.hostname !== 'localhost' && 
                                         window.location.hostname !== '127.0.0.1' && 
                                         !window.location.hostname.startsWith('edumi.co') &&
                                         window.location.hostname.includes('.edumi.co');
                      
                      if (isSubdomain) {
                        navigate('/?section=history');
                      } else {
                        const baseUrl = instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`;
                        navigate(`${baseUrl}?section=history`);
                      }
                      setShowMobileMenu(false);
                    }}
                    className="w-full px-4 py-3 rounded-xl font-medium font-sans transition-all flex items-center gap-3 text-gray-700 hover:bg-gray-50"
                  >
                    <Mail className="w-5 h-5" />
                    ประวัติการชำระเงิน
                  </button>
                </>
              )}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold" style={{ color: themeColor }}>{completedCount}</span>/{totalLessons} บทเรียน
                </div>
                <span className="text-sm font-semibold" style={{ color: themeColor }}>{courseProgress}%</span>
              </div>
              {user ? (
                <button
                  onClick={() => {
                    auth.signOut();
                    setShowMobileMenu(false);
                  }}
                  className="w-full px-4 py-3 bg-red-50 hover:bg-red-100 rounded-xl font-medium font-sans text-red-600 transition-all flex items-center gap-3"
                >
                  <LogOut className="w-5 h-5" />
                  ออกจากระบบ
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setShowMobileMenu(false);
                  }}
                  className="w-full px-4 py-3 rounded-xl font-medium font-sans transition-all text-white flex items-center gap-3 shadow-sm"
                  style={{ background: themeColor }}
                >
                  <User className="w-5 h-5" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Desktop: Two Column Layout | Mobile: Full Width */}
      <div className="lg:flex lg:gap-6 lg:max-w-7xl lg:mx-auto lg:px-6 lg:py-6">
        {/* Left Column - Video & Info (Desktop) | Full Width (Mobile) */}
        <div className="lg:flex-1 lg:max-w-4xl">
          {/* Video Player or Image Cover */}
          <div className="w-full bg-black lg:rounded-2xl lg:overflow-hidden lg:shadow-xl">
            <div className="relative w-full max-w-full" style={{ aspectRatio: '16/9' }}>
              {lesson.contentType === 'image' && lesson.coverImageUrl ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <img
                    src={lesson.coverImageUrl}
                    alt={lesson.title}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <OptimizedVideoPlayer
                  key={lesson.id}
                  originalUrl={lesson.videoUrl}
                  jobId={lesson.videoJobId}
                  mediaId={lesson.videoMediaId}
                  title={lesson.title}
                  className="w-full h-full"
                  isStudentView={true}
                  onTimeUpdate={(time) => setWatchTime(time)}
                  onDurationChange={(dur) => setVideoDuration(dur)}
                />
              )}
            </div>
          </div>

          {/* Current Lesson Info */}
          <div className="bg-white border-b border-gray-100 lg:border-0 lg:rounded-2xl lg:shadow-md lg:mt-4 px-4 lg:px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-gray-900 text-lg lg:text-xl font-semibold mb-3 leading-tight">{lesson.title}</h1>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="px-3 py-1.5 bg-gray-100 rounded-full text-gray-700 font-medium">ตอนที่ {lesson.orderIndex}</span>
                  {isLessonCompleted(lesson.id) ? (
                    <span className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full font-medium">
                      <CheckCircleIcon className="w-4 h-4" />
                      เรียนจบแล้ว
                    </span>
                  ) : (
                    <span className="px-3 py-1.5 rounded-full font-medium text-gray-600 bg-gray-100">
                      กำลังเรียน
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Report Button */}
                <button
                  onClick={() => {
                    setReportingLessonId(lesson.id);
                    setShowReportModal(true);
                  }}
                  className="p-2.5 rounded-xl transition-all hover:bg-red-50 group border border-gray-200 hover:border-red-200"
                  title="รายงานปัญหา"
                >
                  <Flag className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors" />
                </button>
                {!isLessonCompleted(lesson.id) && (
                  <button
                    onClick={markCompleted}
                    className="px-5 py-2.5 rounded-2xl font-medium text-sm transition-all flex items-center gap-2 shadow-sm text-white hover:shadow-md hover:scale-105 transform cursor-pointer"
                    style={{ background: themeColor }}
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    กดเมื่อเรียนจบ
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Lesson List (Desktop Only) | Bottom Section (Mobile) */}
        <div className="lg:w-96 lg:flex-shrink-0">
          <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 lg:bg-white lg:rounded-2xl lg:shadow-xl lg:sticky lg:top-24 px-4 lg:px-5 py-6 lg:max-h-[calc(100vh-7rem)] lg:overflow-hidden lg:flex lg:flex-col">
            <div className="flex items-center justify-between mb-5 lg:pb-4 lg:border-b lg:border-gray-200">
              <h2 className="text-gray-900 font-semibold text-lg lg:text-base flex items-center gap-2">
                <div className="w-8 h-8 lg:w-7 lg:h-7 rounded-xl flex items-center justify-center shadow-sm" style={{ background: themeColor }}>
                  <List className="w-4 h-4 text-white" />
                </div>
                เลือกตอน
              </h2>
              <div className="text-xs font-medium bg-gray-100 px-3 py-1.5 rounded-full" style={{ color: themeColor }}>
                {completedCount}/{totalLessons}
              </div>
            </div>

            <div className="space-y-2 lg:space-y-2 max-h-[60vh] lg:max-h-none lg:flex-1 overflow-y-auto pr-2 custom-scrollbar-light">
            {lessons.map((l) => {
              const isActive = l.id === lesson.id;
              const isCompleted = isLessonCompleted(l.id);
              const isExpanded = expandedLessonId === l.id;
              const hasAttachments = l.attachments && l.attachments.length > 0;

              return (
                <div key={l.id} className={`rounded-xl lg:rounded-lg overflow-hidden transition-all duration-300 border ${
                  isActive ? 'bg-white shadow-lg border-2' : 'bg-white/60 hover:bg-white border-gray-200 hover:shadow-md'
                }`} style={isActive ? { borderColor: themeColor } : {}}>
                  <div
                    className="flex items-center justify-between p-4 lg:p-3 cursor-pointer transition-all"
                  >
                    <Link
                      to={`/courses/${id}/learn/${l.orderIndex}`}
                      className="flex items-center gap-2.5 lg:gap-2 flex-1 min-w-0"
                    >
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {isCompleted ? (
                          <div className="w-11 h-11 lg:w-9 lg:h-9 rounded-2xl lg:rounded-xl bg-emerald-500 flex items-center justify-center shadow-sm">
                            <CheckCircleIcon className="w-5 h-5 lg:w-4 lg:h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-11 h-11 lg:w-9 lg:h-9 rounded-2xl lg:rounded-xl bg-gray-100 flex items-center justify-center border border-gray-200">
                            <PlayIcon className="w-5 h-5 lg:w-4 lg:h-4 text-gray-500" />
                          </div>
                        )}
                      </div>

                      {/* Lesson Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs lg:text-[10px] font-semibold bg-gray-100 px-2 lg:px-1.5 py-0.5 rounded-full" style={{ color: themeColor }}>EP {l.orderIndex}</span>
                          {isActive && (
                            <span className="px-2 lg:px-1.5 py-0.5 text-white text-xs lg:text-[10px] rounded-full font-medium shadow-sm" style={{ background: themeColor }}>
                              กำลังเล่น
                            </span>
                          )}
                        </div>
                        <h3 className="text-gray-900 text-base lg:text-sm font-semibold line-clamp-2 mb-0.5 leading-tight">
                          {l.title}
                        </h3>
                        {l.duration && (
                          <span className="text-xs lg:text-[11px] text-gray-500 inline-block font-medium">
                            {l.duration}
                          </span>
                        )}
                      </div>
                    </Link>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1.5 lg:gap-1">

                      {/* Download Icon - Only show if has attachments */}
                      {hasAttachments && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            toggleLessonExpand(l.id);
                          }}
                          className="flex-shrink-0 p-2 lg:p-1.5 rounded-xl lg:rounded-lg transition-all" style={{ background: isExpanded ? `${themeColor}15` : '#f3f4f6' }}
                        >
                          <Download className={`w-5 h-5 lg:w-4 lg:h-4 transition-all duration-300 ${
                            isExpanded ? 'rotate-180' : ''
                          }`} style={{ color: isExpanded ? themeColor : '#6b7280' }} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Attachments */}
                  {isExpanded && hasAttachments && (
                    <div className="px-4 lg:px-3 pb-4 lg:pb-3 pt-3 lg:pt-2 bg-gray-50 border-t border-gray-100">
                      <div className="space-y-2">
                        {l.attachments.map((file: any, index: number) => (
                          <button
                            key={index}
                            onClick={() => handleFileDownload(file)}
                            className="w-full flex items-center gap-2.5 lg:gap-2 p-3 lg:p-2 rounded-xl lg:rounded-lg transition-all group hover:scale-[1.01] bg-white border border-gray-200 hover:shadow-sm"
                          >
                            <div className="w-9 h-9 lg:w-8 lg:h-8 rounded-xl lg:rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: themeColor }}>
                              <FileText className="w-4 h-4 lg:w-3.5 lg:h-3.5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <p className="text-gray-900 text-sm lg:text-xs truncate">
                                <span className="italic font-light text-gray-500">Download - </span>
                                <span className="font-medium">{file.displayName || "ไฟล์ประกอบการเรียน"}</span>
                              </p>
                              <p className="text-xs lg:text-[10px] text-gray-500 font-medium">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                            <Download className="w-4 h-4 lg:w-3.5 lg:h-3.5 flex-shrink-0 transition-transform group-hover:scale-110" style={{ color: themeColor }} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        themeColor={themeColor}
      />

      {/* Report Issue Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-slideUp">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Flag className="w-5 h-5" style={{ color: themeColor }} />
                รายงานปัญหา
              </h3>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportMessage("");
                  setReportingLessonId(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-all"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                คลิป: <span className="font-semibold text-gray-900">{lessons.find(l => l.id === reportingLessonId)?.title}</span>
              </p>
              <p className="text-xs text-gray-500">
                กรุณาอธิบายปัญหาที่พบ เช่น วิดีโอไม่เล่น, เสียงไม่ชัด, หรือปัญหาอื่นๆ
              </p>
            </div>

            <textarea
              value={reportMessage}
              onChange={(e) => setReportMessage(e.target.value)}
              placeholder="อธิบายปัญหาที่พบ..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-offset-2 transition-all resize-none"
              style={{ focusRingColor: themeColor }}
              rows={4}
            />

            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportMessage("");
                  setReportingLessonId(null);
                }}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-all"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleReportIssue}
                disabled={!reportMessage.trim() || isSubmittingReport}
                className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                style={{ background: themeColor }}
              >
                {isSubmittingReport ? "กำลังส่ง..." : "ส่งรายงาน"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
